/**
 * Email Content Analyzer Service
 * 
 * Now configured to use server-side analysis to protect intellectual property.
 * This client-side service acts as a proxy to the server API.
 */

import { getApiBaseUrl, buildApiUrl } from '../../../utils/apiConfig';

// Simple production check
const isProduction = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'app.sequenceplanner.com' || window.location.hostname === 'sequenceplanner.com';
};

// Export the same interfaces for compatibility with existing code
export interface EmailContent {
  subject: string;
  bodyText: string;
  bodyHtml?: string;
}

export interface AnalysisIssue {
  category: string;
  description: string;
  score: number;
  scoreType: 'spam' | 'promo';
  term?: string;
  location?: string;
  line?: number;
  count?: number;
  position?: {
    start: number;
    end: number;
  };
}

export interface RecommendationInstance {
  term?: string;
  location?: string;
  line?: number;
  count?: number;
  details?: string;
  position?: {
    start: number;
    end: number;
  };
}

export interface Recommendation {
  rule_id: string;
  priority: 'high' | 'medium' | 'low';
  recommendation_text: string;
  why_this_matters: string;
  instances: RecommendationInstance[];
  instanceCount: number;
}

export interface AnalysisResult {
  spamRiskCategory: 'Low' | 'Medium' | 'High' | 'Risky';
  promoLikelihoodCategory: 'Low' | 'Medium' | 'High';
  totalSpamScore: number;
  totalPromoScore: number;
  foundIssues: AnalysisIssue[];
  recommendations?: Recommendation[];
}

// Safe analyzer logger that suppresses implementation details in production
const analyzerLog = (level: 'info' | 'warn' | 'error', message: string, data?: any) => {
  if (isProduction()) {
    // In production, only log errors without sensitive details
    if (level === 'error') {
      console.error(`[EmailAnalyzer] ${message}`);
    }
    return;
  }
  
  // In development, show full logs
  if (level === 'info') {
    console.log(`[EmailAnalyzer] ${message}`, data);
  } else if (level === 'warn') {
    console.warn(`[EmailAnalyzer] ${message}`, data);
  } else if (level === 'error') {
    console.error(`[EmailAnalyzer] ${message}`, data);
  }
};

/**
 * Main function to analyze email content for spam triggers and promo indicators
 * Now uses the server API instead of local analysis
 */
export async function analyzeEmailContent(content: EmailContent): Promise<AnalysisResult> {
  try {
    analyzerLog('info', 'Starting email analysis process');
    
    const endpoint = buildApiUrl('/api/analyze-email');
    analyzerLog('info', `Using endpoint: ${endpoint}`);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for authentication
      body: JSON.stringify(content),
    });

    if (!response.ok) {
      const errorText = await response.text();
      analyzerLog('error', `API error (${response.status})`, errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    analyzerLog('info', 'Analysis completed successfully', isProduction() ? { status: 'Success' } : result);
    
    return result;
  } catch (error) {
    analyzerLog('error', 'Analysis failed', error instanceof Error ? error.message : 'Unknown error');
    throw error;
  }
}

export default analyzeEmailContent; 